import { Injectable } from "@angular/core";
import { camelCase, isEmpty, isDate, isBoolean, isNumber } from "lodash";
import { FeatureService } from '@app/core/services/feature/feature.service'
/**
 * Builds and returns a criteria expression from the "SearchInput" screen that will be shared
 * as a query parameter and can be used as input to the REST services.
 * The criteria is built with parameters, values and operators, below an example:
 * `fullName=='Bruno' && age <= 50`
 *
 * This class should not be modified directly, you can provide your own service which should extend the default one as follows:
 ```
  export class MyCriteriaService extends CriteriaService {
   ...
  }
 ```
 * And provide your class in the `core/custom-core.module.ts` file as follows:
 ```
  providers: [
   { provide: CriteriaService, useClass: MyCriteriaService }
  ]
 ```
 */
@Injectable({
  providedIn: "root"
})
export class CriteriaService {
  constructor(private featureService: FeatureService) { }
  /**
  * Possible values of the number operator
  */
  protected numberOperators = ['==', '!=', '<', '>', '<=', '>='];
  /**
   * Possible values of the date operator
   * */
  protected dateOperators = ['==', '!=', '<', '>', '<=', '>='];
  /**
   * Possible values of the string operator
   * */
  protected stringOperators = ['==', '!=', '~'];
  /**
    * Possible values of the default operator
    * */
  protected defaultOperators = ['==', '!='];
  /**
   * Map column filter operator
   */
  protected opratorMap = {
    'equals': '==',
    'notEquals': '!=',
    'contains': '~',
    'lt': '<',
    'gt': '>',
    'lte': '<=',
    'gte': '>=',
  }
  /**
   * Returns the operator's possible values used in the SearchInput screen
   * @param type
   * @param inColumnFilter
   * @returns {Array}
   */
  getOperatorValues(type?: string, inColumnFilter?: boolean): Array<string> {
    let result;
    let lb;
    switch (type) {
      case 'number':
        result = this.numberOperators;
        break;
      case 'date':
        result = this.dateOperators;
        break;
      case 'text':
      case 'string':
        result = this.stringOperators;
        break;
      default:
        result = this.defaultOperators;
    }
    return result.map(item => {
      switch (item) {
        case '==':
          this.featureService.translatorService.getMessage('Equal').subscribe(
            data => lb = data
          )

          break;
        case '~':
          this.featureService.translatorService.getMessage('Like').subscribe(
            data => lb = data
          )
          break;
        case '!=':
          this.featureService.translatorService.getMessage('Different').subscribe(
            data => lb = data
          )
          break;
        case '<':
          this.featureService.translatorService.getMessage('Less').subscribe(
            data => lb = data
          )
          break;
        case '>':
          this.featureService.translatorService.getMessage('Greater').subscribe(
            data => lb = data
          )
          break;
        case '<=':
          this.featureService.translatorService.getMessage('Less Or Equal').subscribe(
            data => lb = data
          )
          break;
        case '>=':
          this.featureService.translatorService.getMessage('Greater OR Equal').subscribe(
            data => lb = data
          )
          break;
        default:
          lb = item;
      }
      return { label: lb, value: inColumnFilter ? this.getKeyByValue(this.opratorMap, item) : item };
    });
  }
  /**
   *
   * @param object
   * @param value
   * @returns
   */
  getKeyByValue(object, value) {
    return Object.keys(object).find(key => object[key] === value);
  }
  /**
   * Retrieves the criteria to send to the server
   * @param form
   * @param keys
   * @param fromToDateFieldTypeArray
   * @returns {string}
   */
  buildCriteria(
    form: any,
    fromToDateFieldTypeArray?: string[],
    keys?: string
  ): string {
    let criteria: string[] = [];
    const formKeys = Object.keys(form.controls).filter(key => !key.endsWith('Operator'));
    formKeys && formKeys.forEach(item => {
      const controlName = camelCase(item);
      if (form.get(controlName)) {
        const keyValue = form.get(controlName).value;
        const operatorValue = form.get(controlName.concat("Operator"))
          ? form.get(controlName.concat("Operator")).value
          : "==";
        if (keyValue !== undefined && keyValue !== null &&
          (isNumber(keyValue) ||
            isDate(keyValue) ||
            isBoolean(keyValue) ||
            !isEmpty(keyValue))
        ) {
          const subCriteria = this.getCriteria(
            isEmpty(keys) ? item : this.getCriteriaKey(keys, item),
            keyValue,
            operatorValue,
            fromToDateFieldTypeArray
          );
          if (!isEmpty(subCriteria)) {
            criteria.push(subCriteria);
          }
        }
      }
    });
    return criteria.join("&");
  }
  /**
   * Retrieves the criteria to send to the server
   * @param form
   * @param keys
   * @param fromToDateFieldTypeArray
   * @returns {string}
   */
  buildCriteriaWithOr(
    form: any,
    fromToDateFieldTypeArray?: string[],
    keys?: string
  ): string {
    let criteria: string[] = [];
    const formKeys = Object.keys(form.controls).filter(key => !key.endsWith('Operator'));
    formKeys && formKeys.forEach(item => {
      const controlName = camelCase(item);
      if (form.get(controlName)) {
        const keyValue = form.get(controlName).value;
        const operatorValue = form.get(controlName.concat("Operator"))
          ? form.get(controlName.concat("Operator")).value
          : "==";
        if (keyValue !== undefined && keyValue !== null &&
          (isNumber(keyValue) ||
            isDate(keyValue) ||
            isBoolean(keyValue) ||
            !isEmpty(keyValue))
        ) {
          const subCriteria = this.getCriteria(
            isEmpty(keys) ? item : this.getCriteriaKey(keys, item),
            keyValue,
            operatorValue,
            fromToDateFieldTypeArray
          );
          if (!isEmpty(subCriteria)) {
            criteria.push(subCriteria);
          }
        }
      }
    });
    return criteria.join(" | ");
  }
  /**
   * Gets the real key from the flat key passed as parameter
   * @param keys
   * @param flatKey
   * @returns
   */
  private getCriteriaKey(keys, flatKey) {
    return keys.split(',').find(item => flatKey === camelCase(item.split('.').join(' ')))
  }
  /**
   * Retireves the criteria depending on the field type
   * @param key
   * @param value
   * @param operatorValue
   * @param fromToFieldTypeArray
   * @returns {string}
   */
  private getCriteria(
    key: string,
    value: any,
    operatorValue: string,
    fromToFieldTypeArray?: string[]
  ): string {
    if (value !== undefined && value !== null) {
      if (fromToFieldTypeArray && fromToFieldTypeArray.includes(key) && (value.toString().split(',').every((element) => isDate(element)) || isNaN(Number(value)))) {
        return this.getFromToCriteria(value, key, fromToFieldTypeArray);
      }
      if (isDate(value)) {
        return `${key} ${operatorValue} "${this.toIsoString(value)}" `;
      }
      if (!isNaN(Number(value))) {
        return operatorValue === "~" ? `${key} ${operatorValue} "%${value}%"`
          : `${key} ${operatorValue} ${value}`;
      }
      if (typeof value === "boolean") {
        return `${key} ${operatorValue} ${value}`;
      }
      if (typeof value === "string") {
        const likeOperator = operatorValue === "~" ? "%" : "";
        return `${key}  ${operatorValue} "${likeOperator}${value}${likeOperator}"`;
      }
      if (typeof value === "object" && (value.code || value.id)) {
        return value.code ? `${key} ${operatorValue} "${value.code}"` : `${key} ${operatorValue} "${value.id}"`;
      }
      if (Array.isArray(value) && value.length > 0) {
        let filter = `${key} ${operatorValue} "`;
        filter = filter
          .concat(
            value
              .map(item => {
                return item.code ? item.code : item;
              })
              .join(",")
          )
          .concat(`"`);
        return filter;
      }
    }
    return "";
  }
  /**
   * Retrieves the criteria in case of fromTo date
   * @param value
   * @param key
   * @param fromToDateFieldTypeArray
   * @returns {string}
   */
  private getFromToCriteria(
    value: any,
    key: string,
    fromToDateFieldTypeArray
  ) {
    let criteria = "";
    if (value) {
      if (value.length == 2) {
        if (!value[1]) {
          value[1] = (new Date("12/31/2999"))
        }
        if (fromToDateFieldTypeArray.includes(key)) {
          criteria = criteria.concat(
            this.getSubCriteria(value[0], ">=", key)
          );
          criteria =
            criteria !== "" && value[1] ? criteria.concat(" & ") : criteria;
          criteria = criteria.concat(
            this.getSubCriteria(value[1], "<=", key)
          );
        }
      } else {
        criteria = criteria.concat(
          this.getSubCriteria(value[0], "==", key)
        );
      }
    }

    return criteria;
  }

  /**
   * Calculates the date criteria
   * @param value
   * @param operator
   * @param key
   * @returns {string}
   */
  private getSubCriteria(value: any, operator: string, key: string) {
    if (value || value >= 0) {
      return `${key} ${operator} "${!isDate(value) && !isNaN(Number(value)) ? value : this.toIsoString(value)}"`;
    }
    return "";
  }

  /**
   * Calculates the columnFilter criteria
   * @param filters
   * @param fromToCriteriaFields
   * @returns {string}
   */
  protected parseCriteria(filters, fromToCriteriaFields) {
    let result = []
    filters &&
      Object.keys(filters).map(key => {
        const criteria = filters[key][0]
        const value = criteria.value
        if (value !== undefined && value !== null && !Array.isArray(value)) {
          const matchMode = this.opratorMap[criteria.matchMode]
            ? this.opratorMap[criteria.matchMode]
            : criteria.matchMode
          const subCriteria = this.getCriteria(key, value, matchMode, fromToCriteriaFields)
          if (!isEmpty(subCriteria)) {
            result.push(subCriteria)
          }
        }
        if (Array.isArray(value) && value.length > 0) {
          const matchMode = this.opratorMap[criteria.matchMode]
            ? this.opratorMap[criteria.matchMode]
            : criteria.matchMode
          let subsubcriteria = []
          let subCriteria = ""
          value
            .map(item => {
              return item.code ? item.code : item
            })
            .forEach(item => {
              subCriteria =
                value &&
                this.getCriteria(key, item, matchMode, fromToCriteriaFields)
              if (!isEmpty(subCriteria)) {
                subsubcriteria.push(subCriteria)
              }
            })
          if (value.length === 1) {
            result.push(subCriteria)
          } else {
            if (matchMode == "!=") {
              result.push('(' + subsubcriteria.join(' & ') + ')')
            }
            else {
              result.push('(' + subsubcriteria.join(' | ') + ')')

            }

          }
        } else {

        }
      })
    return result.join(' & ')
  }
  /**
   * Calculates the search criteria
   * @param criteria
   * @param filters
   * @param fromToCriteriaFields
   * @returns {string}
   */
  public buildSearchCriteria(criteria, filters, fromToCriteriaFields) {
    if(filters!=null && filters!=undefined){
    let keys = Object.keys(filters)

    keys.forEach(elem => {
      if (filters[elem + ""][0].value == "") { filters[elem + ""][0].value = null }
    })
  }
    let filterCriteria = this.parseCriteria(filters, fromToCriteriaFields)
    if (filterCriteria) {
      let res = ""
      for (let i = 0; i < filterCriteria.length; i++) {
        if (filterCriteria[i] == "&" && filterCriteria[i + 2] != "(") {
          res += "& ignoreCase "
        }
        else {
          res += filterCriteria[i]
        }
      }
      if (filterCriteria.charAt(0) != "(") {
        filterCriteria = "ignoreCase " + res
      } else {
        filterCriteria = res

      }
      if (filterCriteria == "ignoreCase ") {
        filterCriteria = ""
      }
    }

    if (criteria && filterCriteria && !filterCriteria.isEmpty()) {
      return `${criteria} & ${filterCriteria}`
    } else if (criteria) {
      return criteria
    } else {
      return filterCriteria
    }
  }

  /**
   * Calculates the order keys
   * @param event
   * @returns {string}
   */

  public buildOrderKeys(event, order?) {
    const oldOrder =
      order &&
      order
        .split(',')
        .filter(
          value =>
            value
              .slice(0, -1)
              .indexOf(event.multiSortMeta && event.multiSortMeta[0].field) < 0
        )
        .join(',')
    const orderBy =
      event.multiSortMeta &&
      event.multiSortMeta
        .map(value => `${value.field}${value.order === 1 ? '+' : '-'}`)
        .join(',')
    return oldOrder !== (undefined && '')
      ? orderBy
        ? oldOrder + ',' + orderBy
        : oldOrder
      : orderBy
  }

  /**
  * Calculates the sort keys
  * @param event
  * @returns {string}
  */

  public buildSortKeys(multiSortMeta) {
    if (multiSortMeta && !multiSortMeta[0]) {
      multiSortMeta = []
    }
    return (
      multiSortMeta &&
      multiSortMeta
        .map(value => `${value.field}${value.order === 1 ? '+' : '-'}`)
        .join(',')
    )
  }

  toIsoString(date) {
    var tzo = -date.getTimezoneOffset(),
      dif = tzo >= 0 ? '+' : '-',
      pad = function (num) {
        var norm = Math.floor(Math.abs(num));
        return (norm < 10 ? '0' : '') + norm;
      };

    return date.getFullYear() +
      '-' + pad(date.getMonth() + 1) +
      '-' + pad(date.getDate())
  }
}
